// src/components/FloatingChat.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X, Send, RotateCcw, ChevronDown } from "lucide-react";
import { api } from "../services/api";

function Message({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div style={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", marginBottom: 8 }}>
      {!isUser && (
        <div style={{
          width: 24, height: 24, borderRadius: "50%",
          background: "rgba(107,39,55,0.1)", border: "1px solid rgba(107,39,55,0.2)",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginRight: 7, marginTop: 2, flexShrink: 0,
        }}>
          <span style={{ color: "#886070", fontSize: 9, fontWeight: 700 }}>W</span>
        </div>
      )}
      <div style={{
        maxWidth: "78%", padding: "8px 12px",
        borderRadius: isUser ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
        background: isUser ? "#6B2737" : "#E2E8D8",
        color: "#2C3820", fontSize: 13, lineHeight: 1.5,
        border: isUser ? "none" : "1px solid #252525",
        whiteSpace: "pre-wrap",
      }}>
        {msg.content}
      </div>
    </div>
  );
}

function TourSummary({ tour, narrative }) {
  if (!tour && !narrative) return null;
  const roadNames = { "ar-valley": "Ahr Valley", "vienna-venice": "Vienna – Venice", "malopolska": "Małopolska" };
  return (
    <div style={{
      margin: "6px 0", padding: "11px 13px",
      background: "rgba(107,39,55,0.06)", borderRadius: 9,
      border: "1px solid rgba(107,39,55,0.18)",
    }}>
      {tour && (
        <div style={{ marginBottom: narrative ? 7 : 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", color: "#886070", textTransform: "uppercase" }}>
              {roadNames[tour.road_slug] || tour.road_slug}
            </span>
            <span style={{ fontSize: 10, color: "#6A7E58" }}>· {tour.total_days || tour.days}d · {tour.party_size}p</span>
          </div>
          {tour.days && tour.days.slice(0, 2).map((day, i) => (
            <div key={i} style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>
              <strong style={{ color: "#5A6E48" }}>Day {day.day}:</strong> {day.title}
            </div>
          ))}
          {tour.days && tour.days.length > 2 && <div style={{ fontSize: 10, color: "#6A7E58" }}>+{tour.days.length - 2} more…</div>}
        </div>
      )}
      {narrative && <p style={{ fontSize: 12, color: "#888", lineHeight: 1.5, margin: 0 }}>{narrative.slice(0, 180)}{narrative.length > 180 ? "…" : ""}</p>}
    </div>
  );
}

function TypingIndicator() {
  return (
    <div style={{ display: "flex", alignItems: "center", padding: "3px 0", marginBottom: 6 }}>
      <div style={{
        width: 24, height: 24, borderRadius: "50%",
        background: "rgba(107,39,55,0.1)", border: "1px solid rgba(107,39,55,0.2)",
        display: "flex", alignItems: "center", justifyContent: "center", marginRight: 7, flexShrink: 0,
      }}>
        <span style={{ color: "#886070", fontSize: 9, fontWeight: 700 }}>W</span>
      </div>
      {[0,1,2].map(i => (
        <div key={i} style={{
          width: 4, height: 4, borderRadius: "50%", background: "#2E3D48", marginRight: 3,
          animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
        }} />
      ))}
    </div>
  );
}

export default function FloatingChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{
    role: "assistant",
    content: "👋 Привіт! I'm your Wine Route guide.\n\nTell me where you'd like to go — Ahr Valley, Vienna to Venice, or Małopolska — and I'll plan the perfect tour.",
  }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}`);
  const [history, setHistory] = useState([]);
  const [unread, setUnread] = useState(0);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);
  useEffect(() => {
    if (open) { setUnread(0); setTimeout(() => inputRef.current?.focus(), 100); }
  }, [open]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages(prev => [...prev, { role: "user", content: text }]);
    setInput(""); setLoading(true);
    try {
      const res = await api.chat(sessionId, text, history);
      setMessages(prev => [...prev, { role: "assistant", content: res.reply }]);
      setHistory(res.conversation_history || []);
      if (res.tour || res.narrative) setMessages(prev => [...prev, { role: "tour", tour: res.tour, narrative: res.narrative }]);
      if (!open) setUnread(u => u + 1);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, something went wrong. Please try again." }]);
    } finally { setLoading(false); }
  }, [input, loading, sessionId, history, open]);

  const handleKey = useCallback((e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }, [send]);
  const reset = useCallback(() => {
    setMessages([{ role: "assistant", content: "👋 Ready to plan a new tour! Where would you like to go?" }]);
    setHistory([]); setInput("");
  }, []);

  return (
    <>
      <style>{`
        @keyframes bounce{0%,60%,100%{transform:translateY(0)}30%{transform:translateY(-4px)}}
        @keyframes slideUp{from{opacity:0;transform:translateY(14px) scale(0.97)}to{opacity:1;transform:translateY(0) scale(1)}}
        @keyframes pulseRing{0%{transform:scale(1);opacity:0.4}100%{transform:scale(1.5);opacity:0}}
        .chat-drawer{animation:slideUp 0.2s ease-out}
        .chat-fab:hover{box-shadow:0 4px 20px rgba(0,0,0,0.6),0 2px 8px rgba(107,39,55,0.35)!important}
        .chat-send:hover:not(:disabled){background:#7D2F41!important}
        .chat-input:focus{outline:none;border-color:rgba(107,39,55,0.35)!important}
        .chat-scroll::-webkit-scrollbar{width:2px}
        .chat-scroll::-webkit-scrollbar-thumb{background:#2A2A2A;border-radius:2px}
      `}</style>

      <div style={{ position: "fixed", bottom: 22, right: 22, zIndex: 1000 }}>
        {!open && messages.length > 1 && (
          <div style={{
            position: "absolute", inset: -4, borderRadius: "50%",
            background: "rgba(107,39,55,0.25)", animation: "pulseRing 2.5s ease-out infinite",
          }} />
        )}
        <button className="chat-fab" onClick={() => setOpen(o => !o)} style={{
          width: 50, height: 50, borderRadius: "50%",
          background: open ? "#EAEFE2" : "#6B2737",
          border: open ? "1px solid #2A2A2A" : "none",
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 3px 16px rgba(0,0,0,0.5), 0 1px 6px rgba(107,39,55,0.25)",
          transition: "all 0.2s", position: "relative",
        }}>
          {open ? <X size={18} color="#666" /> : <MessageCircle size={18} color="rgba(232,228,223,0.9)" />}
          {!open && unread > 0 && (
            <div style={{
              position: "absolute", top: -1, right: -1, width: 15, height: 15, borderRadius: "50%",
              background: "#8B2A2A", color: "white", fontSize: 9, fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #111",
            }}>{unread}</div>
          )}
        </button>
      </div>

      {open && (
        <div className="chat-drawer" style={{
          position: "fixed", bottom: 84, right: 22,
          width: 340, height: 490, borderRadius: 14,
          background: "#EAEFE2", border: "1px solid #222222",
          boxShadow: "0 8px 40px rgba(0,0,0,0.7)",
          display: "flex", flexDirection: "column", overflow: "hidden", zIndex: 999,
        }}>
          <div style={{
            padding: "11px 13px", background: "#E8EDE0", borderBottom: "1px solid #222",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <div style={{
                width: 30, height: 30, borderRadius: "50%",
                background: "rgba(107,39,55,0.12)", border: "1px solid rgba(107,39,55,0.22)",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14,
              }}>🍷</div>
              <div>
                <div style={{ color: "#2C3820", fontWeight: 600, fontSize: 12 }}>Wine Route Guide</div>
                <div style={{ color: "#2E3D48", fontSize: 10 }}>Plan your perfect tour</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 5 }}>
              {[
                { icon: <RotateCcw size={12} />, action: reset, title: "Start over" },
                { icon: <ChevronDown size={12} />, action: () => setOpen(false), title: "Minimise" },
              ].map((btn, i) => (
                <button key={i} onClick={btn.action} title={btn.title} style={{
                  background: "#E8EDE0", border: "1px solid #2A2A2A", borderRadius: 6,
                  padding: "5px 7px", cursor: "pointer", color: "#6A7E58",
                  display: "flex", alignItems: "center", transition: "color 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.color="#2C3820"}
                onMouseLeave={e => e.currentTarget.style.color="#6A7E58"}
                >{btn.icon}</button>
              ))}
            </div>
          </div>

          <div className="chat-scroll" style={{ flex: 1, overflowY: "auto", padding: "12px 11px 6px", display: "flex", flexDirection: "column" }}>
            {messages.map((msg, i) =>
              msg.role === "tour"
                ? <TourSummary key={i} tour={msg.tour} narrative={msg.narrative} />
                : <Message key={i} msg={msg} />
            )}
            {loading && <TypingIndicator />}
            <div ref={bottomRef} />
          </div>

          <div style={{
            padding: "9px 11px", borderTop: "1px solid #1E1E1E",
            background: "#E8EDE0", display: "flex", gap: 7, alignItems: "flex-end",
          }}>
            <textarea
              ref={inputRef} className="chat-input"
              value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey}
              placeholder="Where would you like to go?" rows={1}
              style={{
                flex: 1, border: "1px solid #222", borderRadius: 9,
                padding: "8px 11px", fontSize: 12,
                fontFamily: "'Inter', sans-serif",
                background: "#E8EDE0", color: "#2C3820",
                resize: "none", lineHeight: 1.45, maxHeight: 72,
                transition: "border-color 0.15s",
              }}
              onInput={e => { e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 72) + "px"; }}
            />
            <button className="chat-send" onClick={send} disabled={!input.trim() || loading} style={{
              width: 34, height: 34, borderRadius: 9,
              background: input.trim() && !loading ? "#6B2737" : "#EAEFE2",
              border: "none",
              cursor: input.trim() && !loading ? "pointer" : "default",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0, transition: "background 0.15s",
            }}>
              <Send size={14} color={input.trim() && !loading ? "rgba(232,228,223,0.9)" : "#333"} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
